import type {
  ConfigurationValidationError,
  TemplateDependencyValidationError,
} from '@festina/configuration-general-types/configuration'
import {
  isEmpty,
  type Merge,
  notEmpty,
  type Optional,
} from '@festina-life/core'

import {
  type AbstractTemplateField,
  type ConfigurationTemplateFunctionValue,
  isConfigurationFunction,
  isDeclaredConfigurationTemplateFunctionValue,
  isSimpleTemplateField,
  isTemplateField,
  type SimpleTemplateField,
  type SubTemplateFieldValue,
  type SupportedEditableTemplateFieldValue,
  type TemplateVersionInfo,
} from '../../types/templateModel'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { isNotInherited } from '../../uiModels/templateOperations/types'
import {
  isTemplateDependencyValidationError,
  type WithOptionalOriginInfo,
} from '../../uiModels/types'
import type { SerialiseValueParams } from '../diffIndicators/ValueSetupDiffIndicators'
import { isAddDynamicFunctionPlaceholder } from '../dynamicFunction/types'
import type { ValidStackableRatios } from '../stackableTable/types'
import {
  functionCall,
  functionCallWithoutModelName,
  functionSignatureToFunctionCallWithReturnType,
} from './functionHelpers'
import type { TemplateFieldChild } from './TemplateFieldsTable'

export type ConfigurationTemplateFunctionValueWithPosition =
  ConfigurationTemplateFunctionValue & { position: number }

export const TEMPLATE_FIELDS_COLUMN_RATIOS = [
  1, 1, 2, 2,
] as ValidStackableRatios[]

export const SIMPLIFIED_TEMPLATE_FIELDS_COLUMN_RATIOS = [
  1,
] as ValidStackableRatios[]

export type IndexedFieldErrors = Record<
  number,
  Record<
    string,
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
>

/**
 * Given an array of TemplateDependencyValidationError this function will index the errors that have an indexedFieldName (validations or variants)
 * such that errors are mapped to a record, firstly by the index in which they're found, and secondly by the field name.
 * We therefore use this to map errors such as: field.validations[0].validation, field.validations[1].key to
 * { 0: { validation: [errors]}, 1: { key: [errors]}}
 */
export const indexedFieldErrors = ({
  fieldPath,
  errors,
  indexedFieldName,
}: {
  fieldPath: string
  errors: ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  indexedFieldName: 'validations' | 'variants'
}): IndexedFieldErrors => {
  const errorsForFieldName = errors.filter(
    ({ error }) => !!error.path?.includes(`${fieldPath}.${indexedFieldName}`),
  )

  return errorsForFieldName.reduce((acc, validationError) => {
    const { error } = validationError
    const errorIndexStr = (error?.path || '')
      .match(/\[\d+\]/)
      ?.at(0)
      ?.slice(1, -1)

    if (errorIndexStr) {
      const errorIndex = Number(errorIndexStr)
      const fieldName = (error.path ?? '').split('.').pop()

      if (fieldName) {
        if (!acc[errorIndex]) {
          acc[errorIndex] = {}
        }

        const errorsForIndex = acc[errorIndex]

        if (!errorsForIndex[fieldName]) {
          errorsForIndex[fieldName] = []
        }

        errorsForIndex[fieldName].push(validationError)
      }
    }

    return acc
  }, {} as IndexedFieldErrors)
}

export const functionExpressionPath = (functionPath: string) => {
  const indexOfDeclaredOrDynamicFunctionNotations =
    functionPath.search(/\.\[(function)\]/g)

  return indexOfDeclaredOrDynamicFunctionNotations !== -1
    ? functionPath.substring(0, indexOfDeclaredOrDynamicFunctionNotations)
    : functionPath
}

export const functionTitle = (
  functionValue: ConfigurationTemplateFunctionValue,
  emptyPlaceholder = '-',
) => {
  if (functionValue.function?.fplusSource) {
    const functionNameFromCall = functionCall(
      functionValue?.function?.fplusSource,
    )

    return isDeclaredConfigurationTemplateFunctionValue(functionValue)
      ? functionCallWithoutModelName(functionNameFromCall)
      : functionNameFromCall
  } else if (
    isDeclaredConfigurationTemplateFunctionValue(functionValue) &&
    functionValue.descriptor?.signature
  ) {
    const functionCallWithModelName =
      functionSignatureToFunctionCallWithReturnType(
        functionValue.descriptor.signature,
      )

    return functionCallWithModelName
      ? functionCallWithoutModelName(functionCallWithModelName)
      : emptyPlaceholder
  }

  return emptyPlaceholder
}

// The rules of diffing with inherited values establish that
//  1. inherited values that are null on the current template,
//  2. Non null values on the current template that are null on the inherited one
// should not be considered as diffs. This function makes the values equal so that the
// diff doesn't show for these cases for the given set of props.
export const mergeInheritedNullProps = <
  T extends WithOptionalOriginInfo<Record<string, unknown>>,
>({
  currentValue,
  inheritedValue,
  keys,
  currentValuePath,
}: {
  currentValue: Optional<T>
  inheritedValue: T
  keys: readonly (keyof T)[]
  currentValuePath: Optional<string>
}): T => {
  const processedValue = {
    ...inheritedValue,
  }

  keys.forEach((processedKey) => {
    const keyPath = currentValuePath
      ? `${currentValuePath}.${processedKey as string}`
      : null

    if (
      !currentValue?.[processedKey] ||
      (
        currentValue[processedKey] as Optional<
          WithOptionalOriginInfo<Record<string, unknown>>
        >
      )?.origin !== keyPath
    ) {
      delete processedValue[processedKey]
    } else if (
      !processedValue[processedKey] &&
      currentValue[processedKey] &&
      (
        currentValue[processedKey] as Optional<
          WithOptionalOriginInfo<Record<string, unknown>>
        >
      )?.origin === keyPath
    ) {
      processedValue[processedKey as keyof T] = currentValue[processedKey]
    }
  })

  return processedValue
}

// Function to merge template references to comply with the rules of diffing.
export const mergeInheritedArrayValues = <
  T extends Merge<
    WithOptionalOriginInfo<Record<string, unknown>>,
    { path: string }
  >,
>({
  currentValue,
  inheritedValue,
  keyFn,
}: {
  currentValue: T[]
  inheritedValue: T[]
  keyFn: (value: T) => string
}) => {
  const inheritedValuesMap = new Map(
    inheritedValue.map((inheritedValue) => [
      keyFn(inheritedValue),
      inheritedValue,
    ]),
  )
  const currentValuesMap = new Map(
    currentValue
      .filter(isNotInherited)
      .map((currentValue) => [keyFn(currentValue), currentValue]),
  )

  const mergedValuesMap = new Map<string, T>()

  for (const [valueKey, value] of currentValuesMap.entries()) {
    const inheritedReferenceForSource = inheritedValuesMap.get(valueKey)

    if (!inheritedReferenceForSource) {
      mergedValuesMap.set(valueKey, value)
    } else {
      mergedValuesMap.set(valueKey, inheritedReferenceForSource)
    }
  }

  for (const [valueKey, value] of inheritedValuesMap.entries()) {
    const currentReferenceForSource = currentValuesMap.get(valueKey)

    if (currentReferenceForSource) {
      mergedValuesMap.set(valueKey, value)
    }
    // Non existent value on the current values map are omitted so that they do not get diffed against
  }

  return Array.from(mergedValuesMap.values())
}

export const MergeableSubTemplateFieldValueProps = [
  'selectable',
  'defaultSelected',
  'parent',
  'documentation',
] as const

export const processSubTemplatedFieldValueForSerialisation = ({
  value,
  valueType,
  currentValue,
  currentValuePath,
}: SerialiseValueParams<SubTemplateFieldValue> & {
  currentValue: Optional<SubTemplateFieldValue>
  currentValuePath: Optional<string>
}) => {
  if (!value) {
    return value
  }

  let serialisableValue: SubTemplateFieldValue = {
    ...value,
    fields: [],
    declaredFunctions: null,
    dynamicFunctions: null,
  }

  if (valueType === 'inherited') {
    serialisableValue = mergeInheritedNullProps({
      currentValue,
      inheritedValue: serialisableValue,
      currentValuePath,
      keys: MergeableSubTemplateFieldValueProps,
    })
  }

  return serialisableValue
}

/**
 * Keys that should be skipped while clearing data in order to avoid infinite loops from circular references
 */
const skipKeys: (keyof AbstractTemplateField<SupportedEditableTemplateFieldValue>)[] =
  [
    'modelPropertyDescriptor',
    'templatePropertyDescriptor',
    'originalPropertyType',
    'parentTemplateType',
  ]

/**
 * Utility functions for clearing all version info fields from a value setup. Required for copying data so that the
 * visual/server model conversion functions don't skip any of the values.
 * @param value
 */
export function clearVersionInfo<T extends null | undefined>(value: T): T
export function clearVersionInfo<T extends Record<string, unknown>>(
  value: WithOptionalOriginInfo<T>,
): T
export function clearVersionInfo<T>(value: WithOptionalOriginInfo<T>[]): T[]
export function clearVersionInfo<T extends string | number | boolean>(
  value: T,
): T

export function clearVersionInfo<T>(
  value:
    | WithOptionalOriginInfo<T>
    | WithOptionalOriginInfo<T>[]
    | string
    | number
    | boolean,
): Optional<T> | T[] {
  if (isEmpty(value)) {
    return
  }

  if (Array.isArray(value)) {
    const nextValues = value.map(clearVersionInfo).filter(notEmpty) as T[]

    return nextValues.length ? nextValues : null
  } else if (typeof value === 'object' && value) {
    const mappedEntries = Object.entries(value).map(([key, keyValue]) => {
      const shouldSkip = skipKeys.some((skippedKey) => skippedKey === key)

      return key === 'versionInfo'
        ? [key, null]
        : [key, shouldSkip ? keyValue : clearVersionInfo(keyValue)]
    })

    return Object.fromEntries(mappedEntries)
  }

  return value as T
}

export const isNonEmptySimpleTemplateField = (
  data: Optional<TemplateFieldChild>,
): data is SimpleTemplateField =>
  !!data && isTemplateField(data) && isSimpleTemplateField(data)

export const isNonEmptyFunctionValue = (
  data: Optional<TemplateFieldChild>,
): data is ConfigurationTemplateFunctionValueWithPosition =>
  !!data &&
  !isAddDynamicFunctionPlaceholder(data) &&
  isConfigurationFunction(data) &&
  !!data.function?.fplusSource

export const isTemplateVersionValidationError = (
  error: ConfigurationValidationError,
  versionInfo: TemplateVersionInfo,
) =>
  isTemplateDependencyValidationError(error) &&
  error.operationName === versionInfo.identifier &&
  error.operationKey === versionInfo.operationKey &&
  (!error.path || error.path === `{${versionInfo.identifier}}`)
